import React, { useEffect, useState } from 'react'
import { getTabletWorkers, pinLogin, getUser } from '../../modules/auth/core/_requests'
import { useAuth } from '../../modules/auth'
import { PinPadModal } from './PinPadModal'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../_metronic/layout/core'
import { useIntl } from 'react-intl'
import { useLang, setLanguage } from '../../../_metronic/i18n/Metronici18n'
import LanguageSelector from '../../modules/auth/components/LanguageSelector'
import { getAsync } from '../../../_metronic/helpers/httpClient'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import { getKioskLandingPath } from './kioskAccess'
import { brandLogo, brandName, bundledLogo, logoErrorFallback } from '../../../_metronic/layout/brandLogo'

const KioskPage: React.FC = () => {
  const [workers, setWorkers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUser, setSelectedUser] = useState<any | null>(null)
  const [showPinModal, setShowPinModal] = useState(false)
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState<string | undefined>()

  const { saveAuth, setCurrentUser, currentUser, logout } = useAuth()
  const navigate = useNavigate()
  const intl = useIntl()
  const lang = useLang()
  const [languages, setLanguages] = useState<any[]>([])

  useEffect(() => {
    const requestLanguages = async () => {
      try {
        const response = await getAsync("/languages")
        if (response && response.data) {
          setLanguages(response.data.map((item) => {
            return ({
              lang: item.code,
              name: item.name,
              flag: toAbsoluteUrl(item.icon),
            })
          }))
        }
      } catch (error) {
        console.error(error)
      }
    }
    requestLanguages();
  }, [])

  const handleLanguageChange = (language: any) => {
    setLanguage(language.lang)
  };

  useEffect(() => {
    // If a user is already logged in, we might want to log them out automatically
    // when they hit the kiosk screen to ensure a clean state for the next worker.
    if (currentUser) {
      logout()
    }

    fetchWorkers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchWorkers = async () => {
    setLoading(true)
    try {
      const { data } = await getTabletWorkers()
      setWorkers(data || [])
    } catch (e) {
      console.error('Failed to fetch tablet workers', e)
      toast.error(intl.formatMessage({ id: 'KIOSK.WORKERS.LOAD_FAILED' }))
    } finally {
      setLoading(false)
    }
  }

  const handleUserClick = (user: any) => {
    setSelectedUser(user)
    setAuthError(undefined)
    setShowPinModal(true)
  }

  const handlePinSubmit = async (pin: string) => {
    if (!selectedUser) return

    setAuthLoading(true)
    setAuthError(undefined)
    try {
      // Step 1: Authenticate with PIN → get token
      const { data: authData } = await pinLogin(selectedUser.id, pin)

      if (authData && authData.api_token) {
        // Step 2: Save token to localStorage + AuthContext (mirrors Login.tsx)
        saveAuth(authData)

        // Step 3: Fetch the full user profile using the new token
        const { data: user } = await getUser()

        // Step 4: Pick the first kiosk page this user's policies allow —
        // the same rules that drive the KioskLayout header buttons.
        const landingPath = getKioskLandingPath(user?.policyIds)
        if (!landingPath) {
          setAuthError(intl.formatMessage({ id: 'KIOSK.AUTH.NO_ACCESS' }))
          saveAuth(undefined)
          return
        }

        // Step 5: Set the user in AuthContext so AppRoutes sees currentUser
        setCurrentUser(user)

        // Step 6: Navigate to the allowed page
        navigate(landingPath)
      } else {
        setAuthError(intl.formatMessage({ id: 'KIOSK.AUTH.INVALID_RESPONSE' }))
      }
    } catch (e: any) {
      console.error('Login failed', e)
      setAuthError(intl.formatMessage({ id: 'KIOSK.PIN_MODAL.ERROR.INVALID_PIN' }))
      saveAuth(undefined)
    } finally {
      setAuthLoading(false)
    }
  }

  return (
     <div className="d-flex flex-column flex-root h-100" style={{ backgroundColor: '#f5f8fa', minHeight: '100vh', position: 'relative' }}>
      <PageTitle>{intl.formatMessage({ id: 'KIOSK.LOGIN.TITLE' })}</PageTitle>

      {/* Language Selector in Top Right */}
      <div className="position-absolute top-0 end-0 p-10 z-index-1">
        <LanguageSelector 
          languages={languages}
          currentLanguageCode={lang}
          onLanguageChange={handleLanguageChange}
        />
      </div>
      
      <div className="d-flex flex-column flex-column-fluid">
        <div className="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20">
          
          <div className="mb-10 text-center">
            <div className="d-flex flex-center mb-8">
              <img
                alt={brandName(process.env.REACT_APP_COMPANY_NAME)}
                src={brandLogo(process.env.REACT_APP_CDN_DOMAIN, 'wide')}
                className="h-60px h-lg-85px"
                onError={logoErrorFallback(bundledLogo('wide'))}
              />
            </div>
            <h1 className="text-dark mb-3" style={{ fontSize: '4rem', fontWeight: '800' }}>
              {intl.formatMessage({ id: 'KIOSK.LOGIN.TITLE' })}
            </h1>
            <div className="text-gray-400 fw-bold fs-3" style={{letterSpacing: '1px'}}>
              {intl.formatMessage({ id: 'KIOSK.LOGIN.SUBTITLE' })}
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center p-20">
              <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="container-fluid" style={{ maxWidth: '1200px' }}>
              <div className="row g-6 g-xl-9 justify-content-center">
                {workers.map((worker) => (
                  <div className="col-md-4 col-xxl-3" key={worker.id}>
                    <div 
                      className="card card-flush shadow-sm hover-elevate-up cursor-pointer h-100" 
                      onClick={() => handleUserClick(worker)}
                      style={{ transition: 'all 0.2s ease', border: '2px solid transparent' }}
                      onMouseEnter={(e: any) => e.currentTarget.style.borderColor = '#009ef7'}
                      onMouseLeave={(e: any) => e.currentTarget.style.borderColor = 'transparent'}
                    >
                      <div className="card-body text-center p-9">
                        <div className="mb-5">
                          <div className="symbol symbol-100px symbol-circle mb-7 shadow-sm">
                            <img 
                              src={worker.avatarLink || '/media/svg/avatars/blank.svg'} 
                              alt={`${worker.name} ${worker.surname}`}
                              onError={(e: any) => { e.target.src = '/media/svg/avatars/blank.svg' }}
                              style={{ objectFit: 'cover' }}
                            />
                          </div>
                        </div>
                        
                        <div className="fs-2 fw-bolder text-gray-800 mb-1">{worker.name}</div>
                        <div className="fs-4 fw-bold text-gray-500">{worker.surname}</div>
                      </div>
                    </div>
                  </div>
                ))}

                {workers.length === 0 && !loading && (
                   <div className="text-center w-100 py-20">
                     <div className="fs-2 fw-bold text-muted mb-4">
                       {intl.formatMessage({ id: 'KIOSK.WORKERS.NOT_FOUND' })}
                     </div>
                     <p className="fs-5 text-gray-500">
                       {intl.formatMessage({ id: 'KIOSK.WORKERS.NOT_FOUND_HINT' })}
                     </p>
                   </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PinPadModal
        show={showPinModal}
        user={selectedUser}
        onClose={() => setShowPinModal(false)}
        onSubmit={handlePinSubmit}
        error={authError}
        loading={authLoading}
      />
    </div>
  )
}

export { KioskPage }
